import type { DemoCase, DemoCaseResponse } from "@rnavoyager/contracts";
import { demoAssetUrl } from "./demo-api";
import type { WorkspaceDirectoryListing, WorkspaceFileEntry } from "./types";

/** 目录项来自已校验的公开清单，不能回退到私有会话接口。 */
export function demoDirectory(
  demo: DemoCase,
  phase: number,
  path: string,
): WorkspaceDirectoryListing {
  if (path === "/project")
    return {
      path,
      truncated: false,
      entries: ["input", "result", "src", "tests"].map((name) => ({
        path: `${path}/${name}`,
        name,
        kind: "directory",
        size: null,
        modified_at: null,
        media_type: null,
        previewable: false,
      })),
    };
  if (
    !/^\/project\/(?:input|result|src|tests)(?:\/[A-Za-z0-9_.-]+)*$/.test(path) ||
    path.split("/").some((part) => part === "." || part === "..")
  )
    throw new Error("案例目录不存在");
  const visible = new Set(
    demo.stages
      .slice(0, Math.max(0, phase + 1))
      .flatMap((stage) =>
        stage.document.blocks.flatMap((block) =>
          block.kind === "artifacts" ? block.items.map((item) => item.path) : [],
        ),
      ),
  );
  const prefix = path.slice("/project/".length) + "/";
  const entries = new Map<string, WorkspaceFileEntry>();
  for (const asset of demo.assets) {
    const input = asset.path.startsWith("input/") && !/summary|completed-results/i.test(asset.name);
    if (phase < 4 && !input && !visible.has(asset.path)) continue;
    if (!asset.path.startsWith(prefix)) continue;
    const rest = asset.path.slice(prefix.length),
      name = rest.split("/")[0];
    const directory = rest.includes("/");
    entries.set(name, {
      path: `${path}/${name}`,
      name,
      kind: directory ? "directory" : "file",
      size: directory ? null : asset.sizeBytes,
      modified_at: null,
      media_type: directory ? null : asset.mediaType,
      previewable:
        !directory &&
        (/\.(cif|pdb)$/i.test(name) ||
          /^(?:text\/(?:plain|csv)|application\/json|image\/)/.test(asset.mediaType)),
    });
  }
  return {
    path,
    truncated: false,
    entries: [...entries.values()].sort(
      (a, b) =>
        Number(b.kind === "directory") - Number(a.kind === "directory") ||
        a.name.localeCompare(b.name),
    ),
  };
}

export function demoFileAccess(snapshot: DemoCaseResponse) {
  const { case: demo, caseDigest } = snapshot;
  const asset = (path: string) => {
    const found = demo.assets.find((file) => file.path === path.replace(/^\/project\//, ""));
    if (!found) throw new Error("案例文件未登记");
    return found;
  };
  const contentUrl = (_session: string, path: string) =>
    demoAssetUrl(demo.caseId, asset(path).assetId, caseDigest);
  return {
    contentUrl,
    downloadUrl: (_session: string, path: string) =>
      demoAssetUrl(demo.caseId, asset(path).assetId, caseDigest, true),
    preview: async (_session: string, path: string, signal?: AbortSignal) => {
      const file = asset(path),
        response = await fetch(contentUrl("", path), { signal });
      if (!response.ok || !response.body) throw new Error("案例文件无法读取");
      const reader = response.body.getReader(),
        chunks: Uint8Array[] = [];
      let length = 0;
      try {
        while (length < 256 * 1024) {
          const next = await reader.read();
          if (next.done) break;
          const chunk = next.value.subarray(0, 256 * 1024 - length);
          chunks.push(chunk);
          length += chunk.length;
        }
      } finally {
        await reader.cancel();
      }
      const bytes = new Uint8Array(length);
      let offset = 0;
      for (const chunk of chunks) {
        bytes.set(chunk, offset);
        offset += chunk.length;
      }
      return {
        path,
        name: file.name,
        media_type: file.mediaType,
        size: file.sizeBytes,
        content: new TextDecoder().decode(bytes),
        truncated: file.sizeBytes > length,
      };
    },
  };
}
